import { SECTIONS } from "../../../../config/constants";
import PageHead from "../../../layout/PageHead";
import {
  Tabs,
  Select,
  Button,
  Form,
  Input,
  InputNumber,
  Drawer,
  Space,
  DrawerProps,
  notification,
  Popover,
} from "antd";
import "./GroupManagement.css";
import {
  EditOutlined,
  CloseOutlined,
  CheckOutlined,
  UsergroupAddOutlined,
  PlusOutlined,
  UserOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import GroupCodesService from "../../../../services/groupcodes/GroupCodesService";
import TablesService from "../../../../services/tables/TablesService";
import Datatable from "../../../shared/components/Datatable";
import { NotificationPlacement } from "antd/lib/notification";
import { ColumnsType } from "antd/lib/table";
import UsersService from "../../../../services/admin/UsersService";
import UserGroupService from "../../../../services/admin/UserGroupsService";
import { UpdateUserGroup } from "../../../../models/admin/UpdateUserGroup";
import { CreateUserGroup } from "../../../../models/admin/CreateUserGroup";
import { useTranslation } from "react-i18next";
import styles from "../group-management/GroupManagement.module.css";
import Search from "antd/lib/input/Search";
import PermissionsService from "../../../../services/admin/PermissionsService";
import { UpsertPermission } from "../../../../models/permissions/UpsertPermission";
import { useSelector } from "react-redux";
import {PERMISSION_METHODS, PERMISSION_METHODS_ARRAY} from "../../../../config/consts/permissions";
import {ROUTES} from "../../../../config/consts/routes";

const { TabPane } = Tabs;

const routes = [
  {
    path: ROUTES.general.main.route,
    breadcrumbName: SECTIONS.general.name,
  },
  {
    breadcrumbName: SECTIONS.administration.name,
  },
  {
    path: ROUTES.administration.groupManagement.route,
    breadcrumbName: ROUTES.administration.groupManagement.name,
  },
];

enum RowAction {
  View = 1,
  Edit,
  Create,
}

const GroupManagement = () => {
  const { t, i18n } = useTranslation("translations");
  const { Option } = Select;
  const { TextArea } = Input;

  const [groupCodes, setGroupCodes] = useState<any>([]);
  const [entities, setEntities] = useState<any>([]);
  const [operations, setOperations] = useState<any>([]);
  const [selectedGroupCode, setSelectedGroupCode] = useState<any>();
  const [selectedEntity, setSelectedEntity] = useState<any>();
  const [selectedOperation, setSelectedOperation] = useState<any>();
  const [data, setData] = useState<DataType[]>([]);
  const [editUserData, setEditUsersData] = useState<EditUSersDataType[]>([]);
  const [permissionsData, setPermissionsData] = useState<UpsertPermission[]>([]);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [drawerAction, setDrawerAction] = useState<RowAction>();
  const [drawerTitle, setDrawerTitle] = useState<string>("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [placement, setPlacement] = useState<DrawerProps["placement"]>("bottom");
  const [loadingButton, setLoadingButton] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [levelInput, setLevelInput] = useState(0);
  const [editedRow, setEditedRow] = useState<any>();
  const [updateRowAllowed, setUpdateRowAllowed] = useState(false);
  const [usersInput, setUsersInput] = useState<any>([]);
  const [selectedUser, setSelectedUser] = useState<any>({});
  const [users, setUsers] = useState<any>([]);
  const [applyAddUsersSearchInput, setApplyAddUsersSearchInput] = useState<string>("");
  const [selectedPermission, setSelectedPermission] = useState<any>({});
  const [filteredPermissionsData, setFilteredPermissionsData] = useState<UpsertPermission[]>([]);

  const [activeTab, setActiveTab] = useState("group-management");

  interface DataType {
    groupId: any;
    groupName: string;
    name: string;
    level: number;
    sysTimeCreated: string;
    sysTimeUpdated: string;
    sysUserCreated: string;
    sysUserUpdated: string;
    users: User[];
  }

  interface EditUSersDataType {
    name: string;
    email: string;
  }

  interface User {
    id: number;
    email: string;
    userName: string;
    sysTimeCreated: string;
    sysTimeUpdated: string;
    sysUserCreated: string;
    sysUserUpdated: string;
    isSocial: boolean;
    isAdmin: boolean;
    password: string;
  }

  useEffect(() => {
    document.title = ROUTES.administration.groupManagement.name;
  }, []);

  const showDrawer = (action: RowAction) => {
    setDrawerAction(action);
    switch (action) {
      case RowAction.View:
        setDrawerTitle("Row details");
        break;
      case RowAction.Create:
        setUsersInput([]);
        setDrawerTitle("New group");
        break;
      case RowAction.Edit:
        setDrawerTitle("Edit group");
        break;
      default:
        break;
    }
    setOpenDrawer(true);
  };

  const onClose = () => {
    setOpenDrawer(false);
    setNameInput("");
    setLevelInput(0);
    setEditedRow(null);
    setPermissionsData([]);
    setUsersInput([]);
    setSelectedUser(null);
    setSelectedPermission({});
    setApplyAddUsersSearchInput("");
  };

  const getUserGroupPermissions = (userGroupId: number) => {
    PermissionsService.get(null, null, userGroupId).then((response) => {
      const groupCodePermissions = response.data.filter(p => p.node === "GROUPCODE");
      const entityPermissions = response.data.filter(p => p.node === "ENTITY");
      
      const groupCodePermissionsMap = groupCodes.map(gc => {
        const permission = groupCodePermissions.find(p => p.fkNode === gc.id);

        return ({
          valueId: gc.id,
          valueName: gc.code,
          id: permission?.id ?? -1,
          object: "USERGROUP",
          objectId: userGroupId,
          isAllowed: permission?.isAllowed ?? false,
          node: "GROUPCODE",
          method: permission?.value ?? "NOT ASSIGNED"
        })
      })

      const enitityPermissionsMap = entities.map(e => {
        const permission = entityPermissions.find(p => p.fkNode === e.id);

        return ({
          valueId: e.id,
          valueName: e.name,
          id: permission?.id ?? -1,
          object: "USERGROUP",
          objectId: userGroupId,
          isAllowed: permission?.isAllowed ?? false,
          node: "ENTITY",
          method: permission?.value ?? "NOT ASSIGNED"
        })
      })

      setPermissionsData(groupCodePermissionsMap.concat(enitityPermissionsMap));
      setFilteredPermissionsData(groupCodePermissionsMap.concat(enitityPermissionsMap));
    })
  }

  const onEditUserGroup = (record: any) => {
    getUserGroupPermissions(record.id);
    showDrawer(RowAction.Edit);
    setNameInput(record.name);
    setLevelInput(record.level);
    setEditedRow(record);
    setUsersInput(record.users.filter(ui => ui));
  };

  const onDoubleClickRow = (record: any) => {
    getUserGroupPermissions(record.id);
    showDrawer(RowAction.View);
    setNameInput(record.name);
    setLevelInput(record.level);
    setEditedRow(record);
    setUsersInput(record.users.filter(ui => ui));
  };

  const onDeleteUserGroup = (id: number) => {
    UserGroupService.remove(id).then((response: any) => {
      setIsTableLoading(true);
      deleteUserGroupNotification("bottomRight");
    });
  };

  const onDeleteUser = (id: number) => {
    setUsersInput(usersInput.filter(u => u.id !== id));
  };

  const onSaveGroup = () => {
    setLoadingButton(true);

    if (editedRow?.id) {
      const model: UpdateUserGroup = {
        Id: editedRow.id,
        Name: nameInput,
        Level: levelInput,
        UserIds: usersInput.map((group) => group.id),
      };
      UserGroupService.update(model)
        .then((response: any) => {
          PermissionsService.upsert(permissionsData)
            .then(() => {
              setIsTableLoading(true);
              onClose();
              setNameInput("");
              setLevelInput(0);
              editUserGroupNotification("bottomRight");
            })
            .catch((error: any) => {
              openFailedNotification(error.response.data, "bottomRight");
            })
            .finally(() => setLoadingButton(false));
        })
        .catch((error: any) => {
          openFailedNotification(error.response.data, "bottomRight");
        })
    } else {
      const model: CreateUserGroup = {
        Name: nameInput,
        Level: levelInput,
        UserIds: usersInput.map((group) => group.id),
      };
      UserGroupService.create(model)
        .then((response: any) => {
          const updatedPermissionsData = permissionsData.map((p) => ({ ...p, objectId: response.data }));
      
          PermissionsService.upsert(updatedPermissionsData)
            .then(() => {
              setIsTableLoading(true);
              onClose();
              setNameInput("");
              setLevelInput(0);
              addUserGroupNotification("bottomRight");
            })
            .catch((error: any) => {
              openFailedNotification(error.response.data, "bottomRight");
            })
            .finally(() => setLoadingButton(false));
        })
        .catch((error: any) => {
          openFailedNotification(error.response.data, "bottomRight");
        })
    }
  };

  const openFailedNotification = (
    description: string,
    placement: NotificationPlacement
  ) => {
    notification.error({
      message: `Error`,
      description: description,
      placement,
    });
  };

  const addUserGroupNotification = (placement: NotificationPlacement) => {
    notification.success({
      message: "User group created successfully",
      placement,
      description:
        "",
      onClick: () => {
        console.log("Notification Clicked!");
      },
    });
  };

  const editUserGroupNotification = (placement: NotificationPlacement) => {
    notification.success({
      message: "User group edited successfully",
      placement,
      description:
        "",
      onClick: () => {
        console.log("Notification Clicked!");
      },
    });
  };

  const deleteUserGroupNotification = (placement: NotificationPlacement) => {
    notification.success({
      message: "User group deleted successfully",
      placement,
      description:
        "",
      onClick: () => {
        console.log("Notification Clicked!");
      },
    });
  };

  useEffect(() => {
    GroupCodesService.getAll().then((response: any) => {
      const data = response.data;
      setGroupCodes(data);
    });
    TablesService.getAllEntities().then((response: any) => {
      const data = response.data;
      setEntities(data);
    });
    TablesService.getAllOperations().then((response: any) => {
      const data = response.data;
      setOperations(data);
    });
  }, []);

  useEffect(() => {
    if (isTableLoading) {
      const promises = [UsersService.getAll(), UserGroupService.getAll()];

      const getData = async () => {
        const data = await Promise.allSettled(promises);
        const users = data[0]["value"]["data"];
        const groups = data[1]["value"]["data"];
        setUsers(users);

        setData(
          groups.map((x: any) => ({
            ...x,
            key: x.id,
          }))
        );
        setIsTableLoading(false);
      };

      getData();
    }
  }, [isTableLoading]);

  useEffect(() => {
    if (editedRow && (nameInput || levelInput)) {
      const def = data.find((x: any) => x.id === editedRow.id);

      if (nameInput != def?.groupName || levelInput != def?.level) {
        setUpdateRowAllowed(true);
      } else {
        setUpdateRowAllowed(false);
      }
    } else {
      setUpdateRowAllowed(false);
    }
  }, [nameInput, levelInput, editedRow]);

  const showUsers = (users: User[]) => {
    return (
      <div>
        {users.map(function (d, idx) {
          return <li key={idx}>{d?.userName}</li>;
        })}
      </div>
    );
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      filters: [],
      filterMode: "tree",
      sorter: (a, b) => a.level - b.level,
    },
    {
      title: "Users",
      dataIndex: "id",
      key: "id",
      filters: [],
      filterMode: "tree",
      render: (val, rec) => (
        <Popover content={() => showUsers(rec.users)} title="Users">
          <Button icon={<UserOutlined />} className={"active-btn"} size="small">
            Users
          </Button>
        </Popover>
      ),
    },
    {
      title: "Created at",
      dataIndex: "sysTimeCreated",
      key: "sysTimeCreated",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.sysTimeCreated.localeCompare(b.sysTimeCreated),
    },
    {
      title: "Modified at",
      dataIndex: "sysTimeUpdated",
      key: "sysTimeUpdated",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.sysTimeUpdated.localeCompare(b.sysTimeUpdated),
    },
    {
      title: "Created by",
      dataIndex: "sysUserCreated",
      key: "sysUserCreated",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.sysTimeCreated.localeCompare(b.sysTimeCreated),
    },
    {
      title: "Modified by",
      dataIndex: "sysUserUpdated",
      key: "sysUserUpdated",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.sysTimeUpdated.localeCompare(b.sysTimeUpdated),
    },
  ];

  const editUsersColumns: ColumnsType<EditUSersDataType> = [
    {
      title: "User Name",
      dataIndex: "userName",
      key: "userName",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
  ];

  const onPermissionMethodChange = (method: any) => {
    const permissions = [...permissionsData];
    const index = permissions.findIndex(p => p.node === selectedPermission.node && p.valueId === selectedPermission.valueId);
    const permission = { ...permissions[index] };
    permission.method = method;
    permissions[index] = permission;
    setPermissionsData(permissions);

    const filteredPermissions = [...filteredPermissionsData];
    const filteredIndex = filteredPermissions.findIndex(p => p.node === selectedPermission.node && p.valueId === selectedPermission.valueId);
    const filteredPermission = { ...filteredPermissions[filteredIndex] };
    filteredPermission.method = method;
    filteredPermissions[filteredIndex] = filteredPermission;
    setFilteredPermissionsData(filteredPermissions);
  }

  const permissionsColumns: ColumnsType<UpsertPermission> = [
    {
      title: "Node",
      dataIndex: "node",
      key: "node",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
    },
        {
      title: "Name",
      dataIndex: "valueName",
      key: "valueName",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
    },
    {
      title: "Type",
      dataIndex: "method",
      key: "method",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      render(value, record, index) {
        if (selectedPermission.valueId !== record.valueId || selectedPermission.objectId !== record.objectId) {
          return value;
        }
      
        return (
          <Select
            allowClear
            showSearch
            style={{ width: "100%" }}
            placeholder="Please select"
            options={PERMISSION_METHODS_ARRAY
              .map(pm => {
                return {
                  label: pm,
                  value: pm,
                }
              })
              .sort((a,b) => a.value.localeCompare(b.value))
            }
            value={
              {
                label: record.method,
                value: record.method,
              }
            }
            onChange={(method) => onPermissionMethodChange(method)}
          />
        )
      },
    },
  ];

  const onEditPermission = (record: any) => {
    setSelectedPermission(record);
  }

  const handleNewGroupButtonClick = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    // setNewRuleOverviewModalEnabled(true);
    getUserGroupPermissions(-1);
    showDrawer(RowAction.Create);
  };

  const groupManagementActionButton = (
    <Button
      // disabled={!selectedEntity || !selectedGroupCode}
      type={"primary"}
      onClick={handleNewGroupButtonClick}
      // overlay={dropdownMenu}
      className="actionButton"
      // placement="bottomRight"
      // arrow={DropdownArrowOptions.}
      // trigger={["click"]}
      // icon={<DownOutlined />}
    >
      <PlusOutlined />
      NEW GROUP
    </Button>
  );

  const scroll = { y: "calc(100vh - 320px)"  };

  const onAddUserClick = () => {
    if (selectedUser) {
      setUsersInput([...usersInput, selectedUser]);
      setSelectedUser(null);
    }
  }

  const onApplyUsersSearch = (value: string) => {
    setFilteredPermissionsData(permissionsData.filter(p =>
      p.valueName.toLowerCase().indexOf(value.toLowerCase()) > -1 ||
      p.node.toLowerCase().indexOf(value.toLowerCase()) > -1));
    setApplyAddUsersSearchInput(value);
  };

  return (
    <div>
      <PageHead
        title={t(
          "Page.Administration.GroupManagement.Headers.GroupManagement",
          {
            lngs: i18n.languages,
            defaultValue: "Group management",
          }
        )}
        icon={<UsergroupAddOutlined />}
        routes={routes}
        groupCodes={groupCodes}
        entities={entities}
        operations={operations}
        selectedGroupCode={selectedGroupCode}
        selectedEntity={selectedEntity}
        selectedOperation={selectedOperation}
        setSelectedGroupCode={setSelectedGroupCode}
        setSelectedEntity={setSelectedEntity}
        setSelectedOperation={setSelectedOperation}
        setSelectGroupCodeDisabled={true}
        setSelectEntityDisabled={true}
        setSelectOperationDisabled={true}
        mainAction={groupManagementActionButton}
        otherActions={[]}
        setActionsDisabled={false}
      />
      <div className="card-container">
        <Tabs
            type="card"
            defaultActiveKey="group-management"
            onChange={(tab) => setActiveTab(tab)}
            style={{height: "100%"}}
        >
          <TabPane
              tab={t("Page.Administration.GroupManagement.Headers.GroupManagement", {
                lngs: i18n.languages,
                defaultValue: "Group management",
              })}
              key="group-management"
          >
            <Datatable
                scroll={scroll}
                tableColumns={columns}
                data={data}
                setData={setData}
                isTableLoading={isTableLoading}
                otherActions={[]}
                otherRowEnabled={false}
                editRowEnabled={true}
                copyRowEnabled={false}
                deleteRowEnabled={true}
                onEditFunc={onEditUserGroup}
                onCopyFunc={undefined}
                onDeleteFunc={onDeleteUserGroup}
                onDoubleClickRow={onDoubleClickRow}
                selectable={false}
                multiSelect={false}
                selectedRows={[]}
                setSelectedRows={undefined}
                paginationDisabled={true}
            ></Datatable>
            {openDrawer && (
                <Drawer
                    width={"400px"}
                    getContainer={false}
                    style={{position: "absolute"}}
                    title={drawerTitle}
                    placement={placement}
                    onClose={onClose}
                    visible={openDrawer}
                    closable={false}
                    height={"550px"}
                    extra={
                      <Space>
                        {RowAction?.View === drawerAction && (
                            <>
                              <Button
                                  onClick={() => showDrawer(RowAction.Edit)}
                                  icon={<EditOutlined/>}
                                  className={"active-btn"}
                                  size="small"
                              >
                                Edit
                              </Button>
                              <Button
                                  type="ghost"
                                  onClick={onClose}
                                  icon={<CloseOutlined/>}
                                  size="small"
                              />
                            </>
                        )}
                        {RowAction?.Create === drawerAction && (
                            <>
                              <Button
                                  disabled={!nameInput || !levelInput}
                                  className={!nameInput || !levelInput ? "" : "active-btn"}
                                  onClick={onSaveGroup}
                                  icon={<CheckOutlined/>}
                                  size="small"
                                  loading={loadingButton}
                              >
                                Save
                              </Button>
                              <Button
                                  type="ghost"
                                  onClick={onClose}
                                  icon={<CloseOutlined/>}
                                  size="small"
                              />
                            </>
                        )}
                        {RowAction?.Edit === drawerAction && (
                            <>
                              <Button
                                  disabled={!updateRowAllowed}
                                  className={!updateRowAllowed ? "" : "active-btn"}
                                  onClick={onSaveGroup}
                                  icon={<CheckOutlined/>}
                                  size="small"
                                  loading={loadingButton}
                              >
                                Update
                              </Button>
                              <Button
                                  type="ghost"
                                  onClick={onClose}
                                  icon={<CloseOutlined/>}
                                  size="small"
                              />
                            </>
                        )}
                      </Space>
                    }
                >
                  <Form
                      disabled={RowAction.View === drawerAction ? true : false}
                      layout="vertical"
                  >
                    <div className={styles.columnsWrap}>
                      <div className={styles.leftColumn}>
                        <Form.Item label="ID:">
                          <Input
                              disabled
                              allowClear
                              value={editedRow?.id}
                              onChange={(e) => setNameInput(e.target.value)}
                          ></Input>
                        </Form.Item>
                        <Form.Item label="Name:">
                          <Input
                              allowClear
                              value={nameInput}
                              placeholder="enter value"
                              onChange={(e) => setNameInput(e.target.value)}
                          ></Input>
                        </Form.Item>
                        <Form.Item label="Level:">
                          <InputNumber
                              value={levelInput}
                              placeholder="enter value"
                              onChange={(value) => setLevelInput(value)}
                          ></InputNumber>
                        </Form.Item>
                      </div>
                      <div className={styles.middleColumn}>
                        <Form.Item label="Add Users:">
                          <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                              }}
                          >
                            <Select
                                allowClear
                                showSearch
                                style={{width: "100%"}}
                                placeholder="Please select"
                                filterSort={(optionA, optionB) =>
                                    (optionA!.label as unknown as string)
                                        .toLowerCase()
                                        .localeCompare(
                                            (optionB!.label as unknown as string).toLowerCase()
                                        )
                                }
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                                }
                                options={users
                                    .filter(u => !usersInput
                                        .find(ui => (ui.id === u.id)))
                                    .map(u => {
                                      return {
                                        label: u.userName,
                                        value: u.id,
                                      }
                                    })}
                                value={
                                  {
                                    label: selectedUser?.userName,
                                    value: selectedUser?.id
                                  }
                                }
                                onChange={(userId) => setSelectedUser(users.find(u => u.id === userId))}
                            />
                            <Button
                                icon={<UserAddOutlined/>}
                                type="primary"
                                className={styles.green_btn}
                                onClick={() => onAddUserClick()}
                            />
                          </div>
                        </Form.Item>
                        <Form.Item label="Edit Users:">
                          <Datatable
                              scroll={scroll}
                              tableColumns={editUsersColumns}
                              data={usersInput}
                              setData={setUsersInput}
                              isTableLoading={isTableLoading}
                              otherActions={[]}
                              otherRowEnabled={false}
                              editRowEnabled={false}
                              copyRowEnabled={false}
                              deleteRowEnabled={true}
                              onEditFunc={undefined}
                              onCopyFunc={undefined}
                              onDeleteFunc={onDeleteUser}
                              onDoubleClickRow={undefined}
                              selectable={false}
                              multiSelect={false}
                              selectedRows={[]}
                              setSelectedRows={undefined}
                          ></Datatable>
                        </Form.Item>
                      </div>
                      <div className={styles.rightColumn}>
                        <Form.Item label="Search by Node:">
                          <Search
                              placeholder={
                                t(
                                    "Page.Administration.GroupManagement.NewGroup.SearchByNode",
                                    {
                                      lngs: i18n.languages,
                                      defaultValue: "Search",
                                    }
                                )!
                              }
                              value={applyAddUsersSearchInput}
                              onChange={(e) => onApplyUsersSearch(e.target.value)}
                          />
                        </Form.Item>
                        <Form.Item label="Permissions:">
                          <Datatable
                              scroll={scroll}
                              tableColumns={permissionsColumns}
                              data={filteredPermissionsData}
                              setData={setPermissionsData}
                              isTableLoading={isTableLoading}
                              otherActions={[]}
                              otherRowEnabled={false}
                              editRowEnabled={true}
                              copyRowEnabled={false}
                              deleteRowEnabled={false}
                              onEditFunc={onEditPermission}
                              onCopyFunc={undefined}
                              onDeleteFunc={undefined}
                              onDoubleClickRow={undefined}
                              selectable={false}
                              multiSelect={false}
                              selectedRows={[]}
                              setSelectedRows={undefined}
                          ></Datatable>
                        </Form.Item>
                      </div>
                    </div>
                  </Form>
                </Drawer>  
            )}
          </TabPane>
        </Tabs>
      </div>
    </div>
);
};

export default GroupManagement;
